import { Link } from '@tanstack/react-router';

import type { AgentRuntime, ReceptionistSettings } from '../../../api/agentRuntimeSchemas';
import { ErrorBanner } from '../../../components/ErrorBanner';
import { LoadingState } from '../../../components/LoadingState';
import { formatMoneyCents } from '../../../lib/format';
import { DotOrb } from '../orb/DotOrb';
import { SettingsForm, SetupSteps } from './SetupSteps';
import { errorText, isRuntimeOff, setupProgress, useAgentRuntime, usePauseToggle, useReceptionistSettings } from './useReception';

export const LUNA = { id: 'recepcion', name: 'Luna', area: 'Recepción', role: 'Contesta el WhatsApp del negocio, da precios y horarios, y reserva cuando hay espacio.' } as const;
const CARD_ORB = 200;
const WHEN = new Intl.DateTimeFormat('es-PA', { weekday: 'short', hour: 'numeric', minute: '2-digit' });
const WHATSAPP_LABEL = { unlinked: 'Sin conectar', pairing: 'Esperando el código', linked: 'Conectado' } as const;

function phrase(runtime: AgentRuntime | undefined, ready: boolean): string {
  if (!runtime) return 'Me estoy preparando para tu negocio.';
  if (runtime.paused) return runtime.pausedReason === 'cap' ? 'Llegué al tope del mes. Vuelvo el día 1.' : 'Estoy en pausa. Nadie recibe respuesta mía.';
  if (!ready) return 'Conéctame y empiezo a contestar.';
  return 'Estoy contestando el WhatsApp del negocio.';
}

function LunaCard({ runtime, ready }: { runtime: AgentRuntime | undefined; ready: boolean }) {
  const toggle = usePauseToggle();
  const usage = runtime?.usage;
  return (
    <section className="team-card" aria-label="Ficha de Luna">
      <div className="team-card-stage">
        <DotOrb size={CARD_ORB} form="stipple" seed={43} onDark fluid />
        <p className="team-card-phrase">{phrase(runtime, ready)}</p>
      </div>
      <p className="status team-card-status" data-tone={runtime?.paused ? 'hold' : ready ? 'good' : 'due'}>
        {runtime?.paused ? 'En pausa' : ready ? 'Atendiendo' : 'Por conectar'}
      </p>
      <dl className="team-card-facts">
        <div>
          <dt>WhatsApp</dt>
          <dd>{runtime ? WHATSAPP_LABEL[runtime.status.whatsapp] : 'Sin conectar'}</dd>
        </div>
        <div>
          <dt>Este mes</dt>
          <dd>{usage ? `${formatMoneyCents(usage.usedCents)} de ${formatMoneyCents(usage.capCents)}` : 'Nada todavía'}</dd>
        </div>
        <div>
          <dt>Última respuesta</dt>
          <dd>{runtime?.status.lastTurnAt ? WHEN.format(new Date(runtime.status.lastTurnAt)) : 'Todavía nada'}</dd>
        </div>
        <div className="team-card-wide">
          <dt>Nivel de permiso</dt>
          <dd>Contesta y reserva dentro de tus reglas; pasa a una persona lo que no le toca.</dd>
        </div>
      </dl>
      {toggle.error ? <ErrorBanner message={errorText(toggle.error, 'No pudimos cambiarlo. Intenta de nuevo.')} /> : null}
      {runtime && ready ? (
        <button type="button" className="btn small secondary" disabled={toggle.isPending || runtime.pausedReason === 'cap'} onClick={() => toggle.mutate(!runtime.paused)}>
          {runtime.paused ? 'Reanudar a Luna' : 'Pausar a Luna'}
        </button>
      ) : null}
    </section>
  );
}

function Working({ settings }: { settings: ReceptionistSettings }) {
  return (
    <div className="reception-working">
      <p className="team-bubble">Estoy atendiendo. Lo que converso con tus clientes queda en Bandeja, y ahí puedes tomar cualquier conversación.</p>
      <Link to="/bandeja" className="btn">
        Abrir Bandeja
      </Link>
      <details className="reception-settings">
        <summary>Cómo atiende Luna</summary>
        <SettingsForm settings={settings} />
      </details>
    </div>
  );
}

/** Luna's card and, beside it, her setup until she is connected, then her daily view. */
export function ReceptionPanel() {
  const settingsQuery = useReceptionistSettings();
  const settings = settingsQuery.data;
  const runtimeQuery = useAgentRuntime(true);
  const runtime = runtimeQuery.data;
  const progress = setupProgress(runtime, Boolean(settings?.updatedAt));
  const ready = progress.whatsapp;
  const off = isRuntimeOff(runtimeQuery.error);

  return (
    <div className="team-workspace">
      <LunaCard runtime={runtime} ready={ready} />
      <section className="team-chat" aria-label="Luna">
        <header className="team-chat-head">
          <h2>{ready && progress.settings ? 'Hoy' : 'Enciende a Luna'}</h2>
          <p className="row-sub">Tres pasos y empieza a contestar.</p>
        </header>
        <div className="team-thread">
          {settingsQuery.isLoading ? <LoadingState label="Llamando a Luna…" /> : null}
          {off ? <p className="notice">Luna se está encendiendo para tu negocio. Mientras tanto puedes dejarle listas tus instrucciones.</p> : null}
          {settingsQuery.error && !isRuntimeOff(settingsQuery.error) ? (
            <ErrorBanner message={errorText(settingsQuery.error, 'No pudimos llamar a Luna. Intenta de nuevo.')} />
          ) : null}
          {settings && ready && progress.settings ? <Working settings={settings} /> : null}
          {settings && !(ready && progress.settings) ? <SetupSteps runtime={runtime} settings={settings} progress={progress} /> : null}
        </div>
      </section>
    </div>
  );
}
